import { useMemo } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, Calendar } from "lucide-react";
import {
  addAccountingLocalDays,
  formatAccountingLocalDate,
  getAccountingWeekStartMonday,
} from "../../filters/config/accountingFilterUtils";
const PERIOD_TYPES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

const BRAND_RED = "#DC1F00";

// weekStartDate/weekEndDate/monthStartDate are plain local-browser calendar-date objects (built via
// `new Date(y, m, d)`, same convention as resolvePeriodRange/formatAccountingLocalDate below) -- they
// aren't real instants, so they must never be reformatted through an explicit app-display timeZone.
// Doing that previously (via formatAppIntlLabel + getAppDisplayTimezone()) reinterpreted local
// midnight in whatever zone the app defaults to (America/New_York), which rolled the displayed day
// back by one whenever the browser's own timezone sits far enough ahead of it (e.g. Colombo/+05:30)
// -- the pill showed "(19 - 25)" while the actual query (built the plain-local way) correctly used
// "20 - 26". Formatting with no timeZone override keeps the label in sync with the real query range.
const monthLabel = (date) => date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

const dayLabel = (date) => date.toLocaleDateString("en-US", { day: "numeric" });

const longDateLabel = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
};

/**
 * Controlled filter bar: Weekly / Monthly / Custom period toggle + date navigator + Division dropdown.
 * `value` shape: { periodType, weekStart, monthStart, customStart, customEnd, divisionId }
 * Dates are 'YYYY-MM-DD' strings. Parent owns the state and derives an actual query range from it.
 */
export default function PeriodFilterBar({ value, onChange, divisions = [], showDivisionFilter = true }) {
  const { periodType, weekStart, monthStart, customStart, customEnd, divisionId } = value;

  const weekStartDate = useMemo(
    () => getAccountingWeekStartMonday(weekStart) || getAccountingWeekStartMonday(new Date()),
    [weekStart]
  );
  const weekEndDate = useMemo(() => addAccountingLocalDays(weekStartDate, 6), [weekStartDate]);
  const weekOfMonth = Math.ceil(weekStartDate.getDate() / 7);

  const monthStartDate = useMemo(() => {
    if (monthStart) {
      const [y, m] = monthStart.split("-").map(Number);
      return new Date(y, m - 1, 1);
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, [monthStart]);

  const setPeriodType = (nextType) => onChange({ ...value, periodType: nextType });

  const goWeek = (dir) => {
    const next = addAccountingLocalDays(weekStartDate, dir * 7);
    onChange({ ...value, weekStart: formatAccountingLocalDate(next) });
  };

  const goMonth = (dir) => {
    const next = new Date(monthStartDate.getFullYear(), monthStartDate.getMonth() + dir, 1);
    onChange({ ...value, monthStart: formatAccountingLocalDate(next) });
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 w-full lg:w-auto">
      <div className="flex flex-col gap-2 w-full lg:w-auto">
        <div className="flex gap-2 w-full lg:w-auto">
          {PERIOD_TYPES.map((p) => {
            const active = periodType === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => setPeriodType(p.value)}
                className="flex-1 lg:flex-none px-3.5 py-1 text-sm font-medium rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#DC1F00]"
                style={active ? { backgroundColor: BRAND_RED, color: "#fff" } : { backgroundColor: "#F1F2F4", color: "#4B5563" }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

      {periodType === "weekly" && (
        <div className="flex items-center justify-between gap-2 w-full lg:w-auto">
          <button type="button" onClick={() => goWeek(-1)} className="p-0.5 rounded-full border hover:bg-gray-50 shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#DC1F00]" style={{ borderColor: BRAND_RED, color: BRAND_RED }} aria-label="Previous week">
            <ChevronLeft size={16} />
          </button>
          <span className="whitespace-nowrap border border-gray-300 rounded-full bg-white px-3 py-1 text-sm text-gray-800">
            {monthLabel(weekStartDate)} Week {String(weekOfMonth).padStart(2, "0")} ({dayLabel(weekStartDate)} - {dayLabel(weekEndDate)})
          </span>
          <button type="button" onClick={() => goWeek(1)} className="p-0.5 rounded-full border hover:bg-gray-50 shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#DC1F00]" style={{ borderColor: BRAND_RED, color: BRAND_RED }} aria-label="Next week">
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {periodType === "monthly" && (
        <div className="flex items-center justify-between gap-2 w-full lg:w-auto">
          <button type="button" onClick={() => goMonth(-1)} className="p-0.5 rounded-full border hover:bg-gray-50 shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#DC1F00]" style={{ borderColor: BRAND_RED, color: BRAND_RED }} aria-label="Previous month">
            <ChevronLeft size={16} />
          </button>
          <span className="whitespace-nowrap border border-gray-300 rounded-full bg-white px-3 py-1 text-sm text-gray-800">
            {monthLabel(monthStartDate)}
          </span>
          <button type="button" onClick={() => goMonth(1)} className="p-0.5 rounded-full border hover:bg-gray-50 shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#DC1F00]" style={{ borderColor: BRAND_RED, color: BRAND_RED }} aria-label="Next month">
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {periodType === "custom" && (
        <div className="flex gap-2 w-full lg:w-auto">
          {[
            { key: "customStart", value: customStart },
            { key: "customEnd", value: customEnd },
          ].map(({ key, value: dateValue }) => (
            <label
              key={key}
              className="relative flex-1 lg:flex-none flex items-center justify-center gap-2 border border-gray-300 rounded-full bg-white pl-3 pr-2 py-1.5 text-sm text-gray-800 cursor-pointer"
            >
              <span className="whitespace-nowrap">{longDateLabel(dateValue) || "Select date"}</span>
              <Calendar size={15} className="shrink-0" style={{ color: BRAND_RED }} />
              <input
                type="date"
                value={dateValue || ""}
                onChange={(e) => onChange({ ...value, [key]: e.target.value })}
                onClick={(e) => {
                  // Native date inputs only auto-open the picker when the browser's own
                  // calendar-icon sub-element is clicked, not the rest of the input box.
                  // Since this input is stretched over the whole pill, force the picker
                  // open on any click within it so the text/icon area works the same way.
                  if (typeof e.currentTarget.showPicker === "function") {
                    try {
                      e.currentTarget.showPicker();
                    } catch {
                      // no-op: e.g. not called from a user gesture in some browsers
                    }
                  }
                }}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </label>
          ))}
        </div>
      )}
      </div>

      {showDivisionFilter && (
        <>
          <div className="hidden lg:block w-px self-stretch bg-gray-200" />

          <div className="flex flex-col gap-2 w-full lg:w-auto">
            <span className="text-xs text-gray-500">Division Filter</span>
            <div className="relative w-full lg:w-auto">
              <select
                value={divisionId || "all"}
                onChange={(e) => onChange({ ...value, divisionId: e.target.value })}
                className="appearance-none rounded-full border text-sm pl-4 pr-8 py-1 bg-white outline-none cursor-pointer w-full lg:w-auto focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#DC1F00]"
                style={{ borderColor: BRAND_RED, color: BRAND_RED }}
              >
                <option value="all">All</option>
                {divisions.map((d) => (
                  <option key={d.division_id} value={d.division_id}>
                    {d.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: BRAND_RED }} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Resolves the filter bar's value into a concrete { startDate, endDate } range (YYYY-MM-DD), for data fetching. */
export function resolvePeriodRange(value) {
  const { periodType, weekStart, monthStart, customStart, customEnd } = value;

  if (periodType === "weekly") {
    const start = getAccountingWeekStartMonday(weekStart) || getAccountingWeekStartMonday(new Date());
    const end = addAccountingLocalDays(start, 6);
    return { startDate: formatAccountingLocalDate(start), endDate: formatAccountingLocalDate(end) };
  }

  if (periodType === "monthly") {
    const [y, m] = (monthStart || formatAccountingLocalDate(new Date())).split("-").map(Number);
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return { startDate: formatAccountingLocalDate(start), endDate: formatAccountingLocalDate(end) };
  }

  return { startDate: customStart || "", endDate: customEnd || "" };
}

export function defaultPeriodFilterValue() {
  const today = new Date();
  return {
    periodType: "weekly",
    weekStart: formatAccountingLocalDate(getAccountingWeekStartMonday(today)),
    monthStart: formatAccountingLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    customStart: formatAccountingLocalDate(addAccountingLocalDays(today, -7)),
    customEnd: formatAccountingLocalDate(today),
    divisionId: "all",
  };
}
